<template>
    <div>
        <div class="mb-2 flex items-center justify-between">
            <!-- Switch para alternar status da transação -->
            <div class="flex items-center space-x-3">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Status:</span>
                <div class="flex items-center space-x-2">
                    <button
                        @click="transactionStatus = 'all'"
                        :class="transactionStatus === 'all' 
                            ? 'bg-red-500 text-white' 
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'"
                        class="px-2 py-1 rounded-md text-xs font-medium transition-colors hover:opacity-80"
                    >
                        Todas
                    </button>
                    <button
                        @click="transactionStatus = 'authorized'"
                        :class="transactionStatus === 'authorized' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'"
                        class="px-2 py-1 rounded-md text-xs font-medium transition-colors hover:opacity-80"
                    >
                        Aprovadas
                    </button>
                </div>
            </div>
            
            <button
                @click="fetchBinUsage"
                :disabled="loading"
                class="flex items-center space-x-1 text-xs text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 transition-colors"
            >
                <ArrowPathIcon :class="{ 'animate-spin': loading }" class="h-3 w-3" />
                <span>{{ loading ? 'Carregando...' : 'Atualizar' }}</span>
            </button>
        </div>
        
        <ticto-loading v-if="loading" />
        
        <div v-else-if="!binData?.summary && (!binData?.top_bins || binData.top_bins.length === 0)" class="text-center py-4 text-gray-500 dark:text-gray-400">
            <div class="text-sm">
                Nenhum BIN de cartão encontrado para transações 
                <span class="font-medium">{{ transactionStatus === 'authorized' ? 'aprovadas' : 'todas' }}</span>
            </div>
        </div>
        
        <div v-else class="space-y-4">
            <!-- Resumo Geral -->
            <div v-if="binData?.summary" class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                    <CreditCardIcon class="h-4 w-4 mr-2" />
                    Resumo Geral dos BINs - 
                    <span class="ml-1" :class="transactionStatus === 'authorized' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'">
                        {{ transactionStatus === 'authorized' ? 'Aprovadas' : 'Todas' }}
                    </span>
                </h4>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="text-center">
                        <div class="text-lg font-bold text-blue-900 dark:text-blue-100">{{ binData.summary.total_unique_bins }}</div>
                        <div class="text-xs text-blue-600 dark:text-blue-300">BINs Únicos</div>
                    </div>
                    <div class="text-center">
                        <div class="text-lg font-bold text-green-900 dark:text-green-100">{{ binData.summary.total_transactions }}</div>
                        <div class="text-xs text-green-600 dark:text-green-300">Transações</div>
                    </div>
                    <div class="text-center">
                        <div class="text-lg font-bold text-purple-900 dark:text-purple-100">{{ $root.formatCurrency(binData.summary.total_amount) }}</div>
                        <div class="text-xs text-purple-600 dark:text-purple-300">Volume Total</div>
                    </div>
                    <div class="text-center" v-if="binData.summary.high_fraud_bins > 0">
                        <div class="text-lg font-bold text-red-900 dark:text-red-100">{{ binData.summary.high_fraud_bins }}</div>
                        <div class="text-xs text-red-600 dark:text-red-300">Alto Risco</div>
                    </div>
                    <div class="text-center" v-else-if="binData.summary.medium_fraud_bins > 0">
                        <div class="text-lg font-bold text-yellow-900 dark:text-yellow-100">{{ binData.summary.medium_fraud_bins }}</div>
                        <div class="text-xs text-yellow-600 dark:text-yellow-300">Médio Risco</div>
                    </div>
                    <div class="text-center" v-else>
                        <div class="text-lg font-bold text-green-900 dark:text-green-100">{{ binData.summary.safe_bins }}</div>
                        <div class="text-xs text-green-600 dark:text-green-300">Seguros</div>
                    </div>
                </div>
                
                <!-- Indicadores de risco quando existem -->
                <div v-if="binData.summary.high_fraud_bins > 0 || binData.summary.medium_fraud_bins > 0 || binData.summary.low_fraud_bins > 0" class="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                    <div class="flex flex-wrap gap-2 justify-center">
                        <span v-if="binData.summary.high_fraud_bins > 0" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            {{ binData.summary.high_fraud_bins }} Alto Risco
                        </span>
                        <span v-if="binData.summary.medium_fraud_bins > 0" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                            {{ binData.summary.medium_fraud_bins }} Médio Risco
                        </span>
                        <span v-if="binData.summary.low_fraud_bins > 0" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                            {{ binData.summary.low_fraud_bins }} Baixo Risco
                        </span>
                        <span v-if="binData.summary.safe_bins > 0" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {{ binData.summary.safe_bins }} Seguros
                        </span>
                    </div>
                </div>
            </div>

            <!-- Top 10 BINs Detalhados -->
            <div v-if="binData?.top_bins && binData.top_bins.length > 0">
                <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    <ChartBarSquareIcon class="h-4 w-4 mr-2" />
                    Top {{ binData.top_bins.length }} BINs Mais Usados -
                    <span class="ml-1" :class="transactionStatus === 'authorized' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'">
                        {{ transactionStatus === 'authorized' ? 'Aprovadas' : 'Todas' }}
                    </span>
                </h4>
                <div class="space-y-2">
                    <div 
                        v-for="(bin, index) in binData.top_bins" 
                        :key="bin.bin"
                        class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg transition-colors"
                        :class="getBinBorderColor(bin, index)"
                    >
                        <div class="flex items-center space-x-3">
                            <div class="flex flex-col items-center">
                                <div class="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold"
                                     :class="getBinRankColor(index)">
                                    {{ index + 1 }}
                                </div>
                            </div>
                            <div class="flex flex-col">
                                <div class="flex items-center space-x-2">
                                    <span class="text-sm font-semibold font-mono text-gray-900 dark:text-gray-100">{{ bin.bin }}****</span>
                                    <span v-if="bin.fraud_level === 'high'" 
                                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 dark:border dark:border-red-500/30">
                                        <ExclamationTriangleIcon class="h-3 w-3 mr-1" />
                                        Fraude Alta ({{ (bin.fraud_ratio * 100).toFixed(1) }}%)
                                    </span>
                                    <span v-else-if="bin.fraud_level === 'medium'" 
                                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border dark:border-yellow-500/30">
                                        <ExclamationTriangleIcon class="h-3 w-3 mr-1" />
                                        Fraude Média ({{ (bin.fraud_ratio * 100).toFixed(1) }}%)
                                    </span>
                                    <span v-else-if="bin.fraud_level === 'low'" 
                                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 dark:border dark:border-orange-500/30">
                                        Fraude Baixa ({{ (bin.fraud_ratio * 100).toFixed(1) }}%)
                                    </span>
                                    <span v-else-if="bin.percentage > 25" 
                                          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 dark:border dark:border-blue-500/30">
                                        Concentração Alta
                                    </span>
                                </div>
                                <div class="flex items-center space-x-2 text-xs">
                                    <div class="flex items-center space-x-1">
                                        <card-brand-icon v-if="bin.brand" :brand="bin.brand" class="!h-4 !w-4" />
                                        <span class="text-gray-500 dark:text-gray-400 uppercase font-semibold">{{ bin.brand || 'N/A' }}</span>
                                    </div>
                                    <span v-if="bin.fraud" class="text-red-600 dark:text-red-400">
                                        • {{ bin.fraud }} fraudes de {{ bin.approved + bin.fraud }} transações
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="flex items-center space-x-4">
                            <div class="text-right">
                                <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ bin.total }} {{ transactionStatus === 'authorized' ? 'vendas' : 'tentativas' }}</div>
                                <div class="text-xs font-medium" :class="getPercentageColor(bin.percentage)">
                                    {{ bin.percentage }}% do total
                                </div>
                            </div>
                            <div class="text-sm font-medium" :class="transactionStatus === 'authorized' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                                {{ $root.formatCurrency(bin.amount) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div v-if="binData?.top_bins && binData.top_bins.length > 0" class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="text-xs text-gray-500 dark:text-gray-400 text-center">
                Exibindo top {{ binData.top_bins.length }} de {{ binData?.summary?.total_unique_bins || binData.top_bins.length }} BINs únicos
                <span class="ml-1" :class="transactionStatus === 'authorized' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                    ({{ transactionStatus === 'authorized' ? 'aprovadas' : 'todas' }})
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { CreditCardIcon, ArrowPathIcon, ExclamationTriangleIcon, ChartBarSquareIcon } from '@heroicons/vue/24/outline'
import TictoLoading from '../layouts/TictoLoading.vue'
import CardBrandIcon from '../layouts/CardBrandIcon.vue'

export default {
    components: {
        CreditCardIcon,
        ArrowPathIcon,
        ExclamationTriangleIcon,
        ChartBarSquareIcon,
        TictoLoading,
        CardBrandIcon
    },
    name: 'UserBinUsageCard',
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    
    data() {
        return {
            loading: false,
            binData: null,
            transactionStatus: 'authorized' // 'authorized' ou 'all'
        };
    },
    
    mounted() {
        // Garante que os dados sejam carregados imediatamente para mostrar o resumo
        this.$nextTick(() => {
            this.fetchBinUsage();
        });
    },
    
    watch: {
        // Recarrega os dados automaticamente quando o status muda
        transactionStatus() {
            this.fetchBinUsage();
        }
    },
    
    methods: {
        async fetchBinUsage() {
            if (!this.user?.id) return;
            
            this.loading = true;
            
            try {
                const { data } = await axios.get(`/api/user/${this.user.id}/bin-usage`, {
                    params: {
                        transaction_status: this.transactionStatus
                    }
                });
                this.binData = data.data || {};
            } catch (error) {
                console.error('Erro ao buscar BINs de cartão:', error);
                this.binData = null;
            } finally {
                this.loading = false;
                // Sempre emite os dados ao final, para garantir que o resumo seja atualizado
                this.$emit('data-loaded', this.binData);
            }
        },
        
        getBinRankColor(index) {
            if (index === 0) return 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-300 dark:border dark:border-yellow-500/50'; // 1º lugar - dourado
            if (index === 1) return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300 dark:border dark:border-gray-500/50';     // 2º lugar - prata
            if (index === 2) return 'bg-orange-200 text-orange-800 dark:bg-orange-800/30 dark:text-orange-300 dark:border dark:border-orange-500/50'; // 3º lugar - bronze
            return 'bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-300 dark:border dark:border-blue-500/50';                      // demais
        },
        
        getBinBorderColor(bin, index) {
            if (bin.fraud_level === 'high') return 'border-red-500 dark:border-red-400';
            if (bin.fraud_level === 'medium') return 'border-orange-500 dark:border-orange-400';
            if (bin.fraud_level === 'low') return 'border-yellow-500 dark:border-yellow-400';
            if (bin.percentage > 25) return 'border-yellow-500 dark:border-yellow-400';
            if (index === 0) return 'border-yellow-400 dark:border-yellow-500';
            return 'border-blue-400 dark:border-blue-500';
        },
        
        getPercentageColor(percentage) {
            if (percentage > 50) return 'text-red-600 dark:text-red-400';
            if (percentage > 25) return 'text-yellow-600 dark:text-yellow-400';
            return 'text-gray-600 dark:text-gray-400';
        }
    }
};
</script> 